import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import client from "../lib/api";
import { Panel, Spinner } from "../components/ui-kit";
import { Plus, X, Trash2, Pencil, Share2, CheckCircle2, Activity } from "lucide-react";
import { toast } from "sonner";
import { ShareTradeModal } from "../components/ShareTradeModal";
import { TradeUpdates } from "../components/vip/TradeUpdates";

const BLANK = { instrument: "Gold", direction: "Buy", status: "running", running_pips: 0, net_pips: 0, outcome: "", entry_lo: "", entry_hi: "", id: null };

export default function VipTrades() {
  const [trades, setTrades] = useState(null);
  const [editing, setEditing] = useState(null); // "new" | id | null
  const [form, setForm] = useState(BLANK);
  const [closing, setClosing] = useState(null); // trade being closed
  const [closeForm, setCloseForm] = useState({ net_pips: 0, outcome: "win" });
  const [quick, setQuick] = useState({}); // id -> running pips draft
  const [share, setShare] = useState(null);

  const load = () => client.get("/vip-trades").then(({ data }) => setTrades(data));
  useEffect(() => { load(); }, []);

  const openAdd = () => { setForm(BLANK); setEditing("new"); };
  const openEdit = (t) => {
    setForm({ instrument: t.instrument, direction: t.direction || "", status: t.status,
      running_pips: t.running_pips ?? 0, net_pips: t.net_pips ?? 0, outcome: t.outcome || "",
      entry_lo: t.entry_lo ?? "", entry_hi: t.entry_hi ?? "", id: t.id });
    setEditing(t.id);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form,
      running_pips: Number(form.running_pips) || 0,
      net_pips: Number(form.net_pips) || 0,
      entry_lo: form.entry_lo === "" ? null : Number(form.entry_lo),
      entry_hi: form.entry_hi === "" ? null : Number(form.entry_hi),
      outcome: form.outcome || undefined };
    try {
      if (form.id) await client.patch(`/vip-trades/${form.id}`, payload);
      else await client.post("/vip-trades", payload);
      toast.success(form.id ? "Trade updated" : "Trade created"); setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const saveRunning = async (id) => {
    try { await client.patch(`/vip-trades/${id}`, { running_pips: Number(quick[id]) || 0 });
      toast.success("Running pips updated"); setQuick((q) => { const n = { ...q }; delete n[id]; return n; }); load(); }
    catch { toast.error("Failed"); }
  };

  const doClose = async (e) => {
    e.preventDefault();
    try { await client.post(`/vip-trades/${closing.id}/close`, { net_pips: Number(closeForm.net_pips) || 0, outcome: closeForm.outcome });
      toast.success("Trade closed"); setClosing(null); load(); }
    catch { toast.error("Failed"); }
  };

  const reopen = async (t) => { try { await client.patch(`/vip-trades/${t.id}`, { status: "running" }); toast.success("Re-opened"); load(); } catch { toast.error("Failed"); } };
  const del = async (id) => { if (!window.confirm("Delete this trade?")) return; try { await client.delete(`/vip-trades/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); } };

  if (!trades) return <Layout title="VIP Trades"><Spinner /></Layout>;
  const running = trades.filter((t) => t.status === "running");
  const closed = trades.filter((t) => t.status === "closed");

  const pipCls = (v) => (Number(v) >= 0 ? "text-emerald-400" : "text-red-400");

  return (
    <Layout title="VIP Trades">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <p className="text-sm text-slate-400">Manually create, close & edit tracked VIP signals. These feed the public results page, win-rate & landing stats.</p>
        <button onClick={openAdd} data-testid="vt-new" className="btn-gold px-4 py-2 text-sm flex items-center justify-center gap-2 whitespace-nowrap"><Plus size={15} /> New Trade</button>
      </div>

      {/* Running */}
      <Panel title={`Running (${running.length})`} testid="vt-running-panel">
        <div className="divide-y divide-[color:var(--border-subtle)]">
          {running.length === 0 && <div className="p-4 text-sm text-slate-500">No running trades.</div>}
          {running.map((t) => (
            <div key={t.id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`vt-run-${t.id}`}>
              <Activity size={15} className="text-emerald-400" />
              <span className="font-head font-semibold text-white w-28">{t.icon} {t.instrument}{t.direction ? ` ${t.direction}` : ""}</span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Running pips</span>
                <input type="number" value={quick[t.id] ?? t.running_pips ?? 0} onChange={(e) => setQuick({ ...quick, [t.id]: e.target.value })}
                  data-testid={`vt-run-pips-${t.id}`} className="w-24 bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-2 py-1 text-sm text-white focus:border-gold focus:outline-none" />
                {quick[t.id] !== undefined && quick[t.id] !== String(t.running_pips) && (
                  <button onClick={() => saveRunning(t.id)} data-testid={`vt-run-save-${t.id}`} className="text-xs btn-gold px-2 py-1">Save</button>
                )}
              </div>
              <div className="ml-auto flex items-center gap-2">
                <button onClick={() => { setClosing(t); setCloseForm({ net_pips: t.running_pips ?? 0, outcome: (t.running_pips ?? 0) >= 0 ? "win" : "loss" }); }} data-testid={`vt-close-${t.id}`} className="text-xs flex items-center gap-1 border border-gold text-gold px-2.5 py-1 hover:bg-[rgba(212,175,55,0.1)] transition-colors duration-200"><CheckCircle2 size={14} /> Close</button>
                <button onClick={() => openEdit(t)} data-testid={`vt-edit-${t.id}`} className="text-slate-400 hover:text-white p-1"><Pencil size={15} /></button>
                <button onClick={() => del(t.id)} data-testid={`vt-del-${t.id}`} className="text-slate-400 hover:text-red-400 p-1"><Trash2 size={15} /></button>
              </div>
              <TradeUpdates trade={t} trades={trades} onMoved={load} />
            </div>
          ))}
        </div>
      </Panel>

      {/* Closed */}
      <div className="mt-5">
      <Panel title={`Closed (${closed.length})`} testid="vt-closed-panel">
        <div className="divide-y divide-[color:var(--border-subtle)]">
          {closed.length === 0 && <div className="p-4 text-sm text-slate-500">No closed trades yet.</div>}
          {closed.map((t) => (
            <div key={t.id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`vt-closed-${t.id}`}>
              <span className="font-head font-semibold text-white w-28">{t.icon} {t.instrument}{t.direction ? ` ${t.direction}` : ""}</span>
              <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border ${t.outcome === "win" ? "text-emerald-400 border-emerald-500/40" : "text-red-400 border-red-500/40"}`}>{t.outcome === "win" ? "Win" : "Loss"}</span>
              <span className={`font-head font-bold text-lg ${pipCls(t.net_pips)}`}>{Number(t.net_pips) >= 0 ? "+" : ""}{t.net_pips}</span>
              <span className="text-xs text-slate-500">{t.closed_date}{t.manual ? " · manual" : ""}</span>
              <div className="ml-auto flex items-center gap-2">
                <button onClick={() => setShare(t)} data-testid={`vt-share-${t.id}`} className="text-slate-400 hover:text-gold p-1" title="Share"><Share2 size={15} /></button>
                <button onClick={() => reopen(t)} data-testid={`vt-reopen-${t.id}`} className="text-xs border border-[color:var(--border-subtle)] text-slate-300 px-2 py-1 hover:border-gold transition-colors duration-200">Re-open</button>
                <button onClick={() => openEdit(t)} data-testid={`vt-edit-${t.id}`} className="text-slate-400 hover:text-white p-1"><Pencil size={15} /></button>
                <button onClick={() => del(t.id)} data-testid={`vt-del-${t.id}`} className="text-slate-400 hover:text-red-400 p-1"><Trash2 size={15} /></button>
              </div>
              <TradeUpdates trade={t} trades={trades} onMoved={load} />
            </div>
          ))}
        </div>
      </Panel>
      </div>

      {/* Create / Edit modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setEditing(null)}>
          <form onSubmit={save} className="surface max-w-md w-full p-5 relative" onClick={(e) => e.stopPropagation()} data-testid="vt-form">
            <button type="button" onClick={() => setEditing(null)} className="absolute top-3 right-3 text-slate-400 hover:text-white"><X size={20} /></button>
            <h3 className="font-head text-lg font-semibold text-white mb-4">{form.id ? "Edit trade" : "New trade"}</h3>
            <div className="space-y-3">
              <div className="flex gap-2">
                <select value={form.instrument} onChange={(e) => setForm({ ...form, instrument: e.target.value })} data-testid="vt-instrument" className="flex-1 bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white focus:border-gold focus:outline-none">
                  <option value="Gold">Gold (XAUUSD)</option><option value="BTC">BTC (BTCUSD)</option>
                </select>
                <select value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value })} data-testid="vt-direction" className="flex-1 bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white focus:border-gold focus:outline-none">
                  <option value="Buy">Buy</option><option value="Sell">Sell</option><option value="">—</option>
                </select>
              </div>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="vt-status" className="w-full bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white focus:border-gold focus:outline-none">
                <option value="running">Running</option><option value="closed">Closed</option>
              </select>
              {form.status === "running" ? (
                <label className="block text-xs text-slate-400">Running pips
                  <input type="number" value={form.running_pips} onChange={(e) => setForm({ ...form, running_pips: e.target.value })} data-testid="vt-f-running" className="mt-1 w-full bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white focus:border-gold focus:outline-none" />
                </label>
              ) : (
                <div className="flex gap-2">
                  <label className="flex-1 block text-xs text-slate-400">Net pips
                    <input type="number" value={form.net_pips} onChange={(e) => setForm({ ...form, net_pips: e.target.value })} data-testid="vt-f-net" className="mt-1 w-full bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white focus:border-gold focus:outline-none" />
                  </label>
                  <label className="flex-1 block text-xs text-slate-400">Outcome
                    <select value={form.outcome || "win"} onChange={(e) => setForm({ ...form, outcome: e.target.value })} data-testid="vt-f-outcome" className="mt-1 w-full bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white focus:border-gold focus:outline-none">
                      <option value="win">Win</option><option value="loss">Loss</option>
                    </select>
                  </label>
                </div>
              )}
              <div className="flex gap-2">
                <input type="number" placeholder="Entry low (opt)" value={form.entry_lo} onChange={(e) => setForm({ ...form, entry_lo: e.target.value })} data-testid="vt-f-entrylo" className="flex-1 bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white focus:border-gold focus:outline-none" />
                <input type="number" placeholder="Entry high (opt)" value={form.entry_hi} onChange={(e) => setForm({ ...form, entry_hi: e.target.value })} data-testid="vt-f-entryhi" className="flex-1 bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white focus:border-gold focus:outline-none" />
              </div>
            </div>
            <button type="submit" data-testid="vt-save" className="btn-gold w-full py-2.5 text-sm mt-4">{form.id ? "Save changes" : "Create trade"}</button>
          </form>
        </div>
      )}

      {/* Close modal */}
      {closing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setClosing(null)}>
          <form onSubmit={doClose} className="surface max-w-sm w-full p-5 relative" onClick={(e) => e.stopPropagation()} data-testid="vt-close-form">
            <button type="button" onClick={() => setClosing(null)} className="absolute top-3 right-3 text-slate-400 hover:text-white"><X size={20} /></button>
            <h3 className="font-head text-lg font-semibold text-white mb-1">Close {closing.icon} {closing.instrument}{closing.direction ? ` ${closing.direction}` : ""}</h3>
            <p className="text-xs text-slate-500 mb-4">Set the final result. Win/Loss auto-picks from pips but you can override.</p>
            <label className="block text-xs text-slate-400">Net pips
              <input type="number" autoFocus value={closeForm.net_pips} onChange={(e) => setCloseForm({ ...closeForm, net_pips: e.target.value, outcome: Number(e.target.value) >= 0 ? "win" : "loss" })} data-testid="vt-close-pips" className="mt-1 w-full bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white focus:border-gold focus:outline-none" />
            </label>
            <div className="flex gap-2 mt-3">
              {["win", "loss"].map((o) => (
                <button type="button" key={o} onClick={() => setCloseForm({ ...closeForm, outcome: o })} data-testid={`vt-close-${o}`} className={`flex-1 py-2 text-sm border transition-colors duration-200 ${closeForm.outcome === o ? (o === "win" ? "border-emerald-500 text-emerald-400" : "border-red-500 text-red-400") : "border-[color:var(--border-subtle)] text-slate-400"}`}>{o === "win" ? "Win ✅" : "Loss ❌"}</button>
              ))}
            </div>
            <button type="submit" data-testid="vt-close-confirm" className="btn-gold w-full py-2.5 text-sm mt-4">Close trade</button>
          </form>
        </div>
      )}

      <ShareTradeModal trade={share} onClose={() => setShare(null)} />
    </Layout>
  );
}
